import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DataTableSkeleton, PageHeader, SectionCard, StatCard } from "components/admin";
import { DataTable } from "components/molecules/table/table";
import { FollowAthleteForm } from "pages/app/workspace/sections/forms/followed-athletes-section-form";
import { validateFollowAthleteSelection } from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  SectionContentGrid,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { getApiErrorMessage } from "utils/admin";
import { formatPortalLabel } from "utils/portal-access";
import {
  FollowedAthletesSectionSection,
  FollowedAthletesSectionInfoRow,
  FollowedAthletesSectionThumbnailImage,
  FollowedAthletesSectionLabelText,
  FollowedAthletesSectionMetaText,
} from "pages/app/workspace/sections/styles/followed-athletes-section.inline.styles";
export const FollowedAthletesSection: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const queryClient = useQueryClient();
  const [selectedAthleteId, setSelectedAthleteId] = useState("");
  const followedQuery = useQuery({
    queryKey: ["workspace-followed-athletes"],
    queryFn: () => workspaceApi.users.followedAthletes(),
  });
  const athletesQuery = useQuery({
    queryKey: ["workspace-follow-athletes-reference"],
    queryFn: () => workspaceApi.athletes.list(),
  });
  const followMutation = useMutation({
    mutationFn: () => workspaceApi.users.followAthlete(currentUser.id, selectedAthleteId),
    onSuccess: () => {
      toast.success("Athlete followed.");
      setSelectedAthleteId("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-followed-athletes"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to follow this athlete."));
    },
  });
  const unfollowMutation = useMutation({
    mutationFn: (playerId: string | number) =>
      workspaceApi.users.unfollowAthlete(currentUser.id, playerId),
    onSuccess: () => {
      toast.success("Athlete unfollowed.");
      queryClient.invalidateQueries({
        queryKey: ["workspace-followed-athletes"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to unfollow this athlete."));
    },
  });
  const followedAthletes = followedQuery.data || [];
  const athletes = athletesQuery.data || [];
  const followErrors = validateFollowAthleteSelection(selectedAthleteId);
  const handleFollowSubmit = () => {
    if (followErrors.selectedAthleteId) {
      toast.error(followErrors.selectedAthleteId);
      return;
    }
    followMutation.mutate();
  };
  return (
    <>
      <PageHeader
        title="Followed Athletes"
        description="Keep your followed-athlete feed aligned with the API by adding or removing follows directly from this workspace."
      />

      <SectionStatsGrid>
        <StatCard label="Followed" value={followedAthletes.length} />
        <StatCard label="Available Athletes" value={athletes.length} />
        <StatCard
          label="Primary Follow"
          value={followedAthletes[0]?.full_name || "None selected"}
        />
        <StatCard label="Profile Role" value={formatPortalLabel(currentUser?.role)} />
      </SectionStatsGrid>

      <FollowedAthletesSectionSection>
        <SectionContentGrid>
          <SectionCard subtitle="Create a follow record" title="Follow Athlete">
            <FollowAthleteForm
              athletes={athletes}
              athleteError={followErrors.selectedAthleteId}
              isSubmitting={followMutation.isPending}
              onAthleteChange={setSelectedAthleteId}
              onSubmit={handleFollowSubmit}
              selectedAthleteId={selectedAthleteId}
            />
          </SectionCard>

          <SectionCard subtitle="Current follow records" title="My Followed Athletes">
            <DataTable
              canEdit={false}
              columns={[
                {
                  label: "Athlete",
                  accessor: (row: any) => (
                    <FollowedAthletesSectionInfoRow>
                      <FollowedAthletesSectionThumbnailImage
                        alt={row.full_name || "Athlete"}
                        fallbackLabel={row.full_name}
                        src={row.avatar_url || row.image}
                        variant="athlete"
                      />
                      <div>
                        <FollowedAthletesSectionLabelText>
                          {row.full_name}
                        </FollowedAthletesSectionLabelText>
                        <FollowedAthletesSectionMetaText>
                          {row.position || "Position pending"}
                        </FollowedAthletesSectionMetaText>
                      </div>
                    </FollowedAthletesSectionInfoRow>
                  ),
                },
                {
                  label: "Team",
                  accessor: (row: any) =>
                    row.primary_team?.name || row.team_names?.[0] || "Unknown",
                },
                {
                  label: "Followers",
                  accessor: (row: any) => row.follower_count ?? 0,
                },
              ]}
              data={followedAthletes}
              emptyDescription="Followed athletes will appear here once you add them."
              emptyTitle="No followed athletes yet"
              hideNav
              isLoading={followedQuery.isLoading}
              loadingState={<DataTableSkeleton columns={3} rows={5} showThumbnail />}
              onRemove={(id, row) => {
                const targetId = row?.id || id;
                if (window.confirm("Unfollow this athlete?")) {
                  unfollowMutation.mutate(String(targetId));
                }
              }}
            />
          </SectionCard>
        </SectionContentGrid>
      </FollowedAthletesSectionSection>
    </>
  );
};
